import { createRoute, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp } from "lucide-react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import tableStyles from "../components/ui/Table/Table.module.css";
import { Button } from "../components/ui/Button/Button";
import { Modal } from "../components/ui/Modal/Modal";
import { FormField } from "../components/ui/FormField/FormField";
import { Input } from "../components/ui/Input/Input";
import { SearchableSelect } from "../components/ui/SearchableSelect/SearchableSelect";
import type { SearchableSelectOption } from "../components/ui/SearchableSelect/SearchableSelect";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { Badge } from "../components/ui/Badge/Badge";
import { api } from "../lib/api";
import { formatDate } from "../lib/date";
import {
	useLoadReportList,
	useCreateLoadReport,
	useUpdateLoadReport,
	useUpdateLoadReportStatus,
	useDeleteLoadReport,
} from "../hooks/use-load-reports";
import type { LoadReport } from "../hooks/use-load-reports";
import styles from "./LoadReports.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/load-reports",
	component: withPermission(LoadReportsPage, "load_reports", "read"),
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		status: String(search.status || ""),
		search: String(search.search || ""),
	}),
});

const STATUS_OPTIONS = [
	{ value: "waiting", label: "Bekliyor" },
	{ value: "arrived", label: "Varış Gerçekleşti" },
	{ value: "customs_processing", label: "Gümrük İşlemleri Gerçekleşti" },
	{ value: "customs_completed", label: "Gümrük İşlemleri Tamamlandı" },
];

const statusVariant: Record<string, string> = {
	waiting: "default",
	arrived: "warning",
	customs_processing: "success",
	customs_completed: "success",
};

function StatusDropdown({ id, current }: { id: string; current: string }) {
	const [open, setOpen] = useState(false);
	const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
	const wrapperRef = useRef<HTMLDivElement>(null);
	const updateStatus = useUpdateLoadReportStatus();

	useEffect(() => {
		function handleClick(e: MouseEvent) {
			if (wrapperRef.current?.contains(e.target as Node)) return;
			setOpen(false);
		}
		if (open) document.addEventListener("click", handleClick);
		return () => document.removeEventListener("click", handleClick);
	}, [open]);

	const handleOpen = () => {
		if (wrapperRef.current) {
			const rect = wrapperRef.current.getBoundingClientRect();
			setMenuPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
		}
		setOpen((o) => !o);
	};

	return (
		<div ref={wrapperRef}>
			<Button style={{ background: "#2563eb", color: "#fff" }} size="sm" onClick={handleOpen}>
				Durum Güncelle
			</Button>
			{open &&
				createPortal(
					<div
						className={styles.statusDropdownMenu}
						style={{ position: "absolute", top: menuPos.top, left: menuPos.left }}
					>
						{STATUS_OPTIONS.map((s) => (
							<button
								key={s.value}
								type="button"
								className={[
									styles.statusDropdownItem,
									s.value === current && styles.statusDropdownItemActive,
								]
									.filter(Boolean)
									.join(" ")}
								onClick={() => {
									updateStatus.mutate({ id, status: s.value });
									setOpen(false);
								}}
							>
								{s.label}
							</button>
						))}
					</div>,
					document.body,
				)}
		</div>
	);
}

function LoadReportsPage() {
	const { t } = useTranslation();
	const searchParams = route.useSearch();
	const page = searchParams.page;
	const pageSize = searchParams.size;
	const status = searchParams.status;
	const searchQuery = searchParams.search;

	const navigate = useNavigate();

	const { data, isLoading, isError, refetch } = useLoadReportList({
		...(status ? { status } : {}),
		...(searchQuery ? { search: searchQuery } : {}),
		page,
		limit: pageSize,
	});

	const createReport = useCreateLoadReport();
	const updateReport = useUpdateLoadReport();
	const deleteReport = useDeleteLoadReport();

	const items = data?.items ?? [];
	const total = data?.total ?? 0;

	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [searchInput, setSearchInput] = useState(searchQuery);
	const [mode, setMode] = useState<"international" | "domestic">("international");
	const emptyForm = {
		companyId: "",
		stockItemId: "",
		sapDocEntry: "",
		sapDocNum: "",
		supplierCardCode: "",
		supplierName: "",
		reservationNo: "",
		shipperName: "",
		departurePort: "",
		arrivalPort: "",
		bookingNo: "",
		hblNo: "",
		vesselName: "",
		departureDate: "",
		estimatedArrivalDate: "",
		equipmentType: "",
		notes: "",
	};
	const [form, setForm] = useState(emptyForm);
	const [formError, setFormError] = useState("");
	interface IntlOrderLine {
		lineNum: number;
		itemCode: string;
		description: string;
		quantity: number;
		openQty: number;
		unitPrice: number;
		lineTotal: number;
		warehouse: string | null;
	}
	const intlOrdersRef = useRef<
		Record<string, { docNum: number; cardCode: string; cardName: string; lines: IntlOrderLine[] }>
	>({});
	const [orderLines, setOrderLines] = useState<IntlOrderLine[]>([]);
	const [selectedLines, setSelectedLines] = useState<Record<number, string>>({});

	const loadCompanies = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
		const res = await api.get<{ items: Array<{ id: string; legalName: string }> }>(
			`/api/companies?limit=50&all=true${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		console.log("[loadCompanies] res:", res);
		return (res.items ?? []).map((c) => ({ value: c.id, label: c.legalName }));
	}, []);

	const loadStockItems = useCallback(async (query: string): Promise<SearchableSelectOption[]> => {
		const res = await api.get<{ items: Array<{ id: string; code: string; name: string }> }>(
			`/api/stock-items?limit=50&activeOnly=true${query ? `&search=${encodeURIComponent(query)}` : ""}`,
		);
		return res.items.map((s) => ({ value: s.id, label: `${s.code} – ${s.name}` }));
	}, []);

	const loadInternationalOrders = useCallback(
		async (query: string): Promise<SearchableSelectOption[]> => {
			const res = await api.get<{
				data: Array<{
					docEntry: number;
					docNum: number;
					cardCode: string;
					cardName: string;
					lines: IntlOrderLine[];
				}>;
			}>(
				`/api/sap/international-orders?pageSize=50${query ? `&search=${encodeURIComponent(query)}` : ""}`,
			);
			for (const o of res.data ?? []) {
				intlOrdersRef.current[String(o.docEntry)] = {
					docNum: o.docNum,
					cardCode: o.cardCode,
					cardName: o.cardName,
					lines: o.lines ?? [],
				};
			}
			return (res.data ?? []).map((o) => ({
				value: String(o.docEntry),
				label: `#${o.docNum} – ${o.cardName}`,
			}));
		},
		[],
	);

	const openCreateModal = () => {
		setEditingId(null);
		setFormError("");
		setMode("international");
		setForm(emptyForm);
		setOrderLines([]);
		setSelectedLines({});
		setModalOpen(true);
	};

	const openEditModal = async (item: LoadReport) => {
		setEditingId(item.id);
		setFormError("");
		const editMode: "international" | "domestic" = item.sapDocEntry ? "international" : "domestic";
		setMode(editMode);
		setForm({
			companyId: item.companyId ?? "",
			stockItemId: item.stockItemId ?? "",
			sapDocEntry: item.sapDocEntry ? String(item.sapDocEntry) : "",
			sapDocNum: item.sapDocNum ? String(item.sapDocNum) : "",
			supplierCardCode: item.supplierCardCode ?? "",
			supplierName: item.supplierName ?? "",
			reservationNo: item.reservationNo ?? "",
			shipperName: item.shipperName ?? "",
			departurePort: item.departurePort ?? "",
			arrivalPort: item.arrivalPort ?? "",
			bookingNo: item.bookingNo ?? "",
			hblNo: item.hblNo ?? "",
			vesselName: item.vesselName ?? "",
			departureDate: item.departureDate?.slice(0, 10) ?? "",
			estimatedArrivalDate: item.estimatedArrivalDate?.slice(0, 10) ?? "",
			equipmentType: item.equipmentType ?? "",
			notes: item.notes ?? "",
		});
		setOrderLines([]);
		setSelectedLines({});
		setModalOpen(true);
		if (editMode === "international" && item.sapDocNum) {
			const res = await api.get<{
				data: Array<{
					docEntry: number;
					docNum: number;
					cardCode: string;
					cardName: string;
					lines: IntlOrderLine[];
				}>;
			}>(
				`/api/sap/international-orders?pageSize=50&search=${encodeURIComponent(String(item.sapDocNum))}`,
			);
			const match = (res.data ?? []).find((o) => o.docEntry === item.sapDocEntry) ?? res.data?.[0];
			if (match) {
				intlOrdersRef.current[String(match.docEntry)] = {
					docNum: match.docNum,
					cardCode: match.cardCode,
					cardName: match.cardName,
					lines: match.lines ?? [],
				};
				setOrderLines(match.lines ?? []);
				const preselected: Record<number, string> = {};
				for (const line of item.lines ?? []) {
					if (line.lineNum != null) preselected[line.lineNum] = line.quantity;
				}
				setSelectedLines(preselected);
			}
		}
	};

	const toggleOrderLine = (line: IntlOrderLine, checked: boolean) => {
		setSelectedLines((prev) => {
			const next = { ...prev };
			if (checked) {
				next[line.lineNum] = String(line.openQty || line.quantity);
			} else {
				delete next[line.lineNum];
			}
			return next;
		});
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setFormError("");
		if (!form.departureDate || !form.estimatedArrivalDate || !form.equipmentType) {
			setFormError("Çıkış tarihi, tahmini varış tarihi ve ekipman tipi zorunludur.");
			return;
		}
		if (mode === "international" && !form.sapDocEntry) {
			setFormError("Yurtdışı sipariş seçilmelidir.");
			return;
		}
		if (mode === "domestic" && !form.companyId) {
			setFormError("Firma seçilmelidir.");
			return;
		}
		const lines =
			mode === "international"
				? orderLines
						.filter((l) => selectedLines[l.lineNum] && Number(selectedLines[l.lineNum]) > 0)
						.map((l) => ({
							lineNum: l.lineNum,
							itemCode: l.itemCode,
							itemDescription: l.description,
							quantity: Number(selectedLines[l.lineNum]),
							...(l.warehouse ? { warehouse: l.warehouse } : {}),
						}))
				: [];
		const payload = {
			...(mode === "domestic" && form.companyId ? { companyId: form.companyId } : {}),
			...(form.stockItemId ? { stockItemId: form.stockItemId } : {}),
			...(mode === "international" && form.sapDocEntry
				? {
						sapDocEntry: Number(form.sapDocEntry),
						...(form.sapDocNum ? { sapDocNum: Number(form.sapDocNum) } : {}),
						...(form.supplierCardCode ? { supplierCardCode: form.supplierCardCode } : {}),
						...(form.supplierName ? { supplierName: form.supplierName } : {}),
					}
				: {}),
			...(form.reservationNo ? { reservationNo: form.reservationNo } : {}),
			...(form.shipperName ? { shipperName: form.shipperName } : {}),
			...(form.departurePort ? { departurePort: form.departurePort } : {}),
			...(form.arrivalPort ? { arrivalPort: form.arrivalPort } : {}),
			...(form.bookingNo ? { bookingNo: form.bookingNo } : {}),
			...(form.hblNo ? { hblNo: form.hblNo } : {}),
			...(form.vesselName ? { vesselName: form.vesselName } : {}),
			departureDate: form.departureDate,
			estimatedArrivalDate: form.estimatedArrivalDate,
			equipmentType: form.equipmentType,
			...(form.notes ? { notes: form.notes } : {}),
			...(lines.length > 0 ? { lines } : {}),
		};
		try {
			if (editingId) {
				await updateReport.mutateAsync({ id: editingId, data: payload });
			} else {
				await createReport.mutateAsync(payload);
			}
			setModalOpen(false);
			setForm(emptyForm);
			setOrderLines([]);
			setSelectedLines({});
			setEditingId(null);
		} catch {
			setFormError(
				editingId
					? "Kayıt güncellenirken bir hata oluştu."
					: "Kayıt oluşturulurken bir hata oluştu.",
			);
		}
	};

	if (isLoading) return <LoadingSpinner />;
	if (isError) return <ErrorFallback onRetry={refetch} />;

	return (
		<div>
			<PageHeader
				title="Yük Raporu"
				action={<Button onClick={openCreateModal}>Yeni Yük Raporu</Button>}
			/>

			<div className={styles.filters}>
				<div className={styles.filterRow}>
					<select
						className={styles.filterSelect}
						value={status}
						onChange={(e) =>
							navigate({
								to: "/load-reports",
								search: { status: e.target.value, page: 1, size: pageSize, search: searchQuery },
							})
						}
					>
						<option value="">Tüm Durumlar</option>
						{STATUS_OPTIONS.map((s) => (
							<option key={s.value} value={s.value}>
								{s.label}
							</option>
						))}
					</select>
					<input
						className={styles.searchInput}
						placeholder="Firma, tedarikçi, rezervasyon, ürün ara..."
						value={searchInput}
						onChange={(e) => setSearchInput(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === "Enter")
								navigate({
									to: "/load-reports",
									search: { status, page: 1, size: pageSize, search: searchInput },
								});
						}}
					/>
				</div>
			</div>

			<Card>
				{items.length === 0 ? (
					<p style={{ color: "var(--color-fg-muted)", padding: "var(--space-4)" }}>{t("noData")}</p>
				) : (
					<div className={tableStyles.wrapper}>
						<table className={tableStyles.table}>
							<thead>
								<tr>
									<th style={{ width: 40 }}></th>
									<th>Firma / Tedarikçi</th>
									<th>Sipariş / Rez.</th>
									<th>Ürün</th>
									<th>Çıkış Tarihi</th>
									<th>Tahmini Varış</th>
									<th>Ekipman Tipi</th>
									<th>Durum</th>
									<th>İşlemler</th>
								</tr>
							</thead>
							<tbody>
								{items.map((item) => {
									const lines = item.lines ?? [];
									const isExpanded = expandedId === item.id;
									return (
										<>
											<tr key={item.id}>
												<td>
													{lines.length > 0 && (
														<button
															type="button"
															onClick={() => setExpandedId(isExpanded ? null : item.id)}
															style={{
																background: "none",
																border: "none",
																cursor: "pointer",
																display: "flex",
																alignItems: "center",
																color: "var(--color-fg-muted)",
															}}
														>
															{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
														</button>
													)}
												</td>
												<td data-label="Firma / Tedarikçi">
													{item.companyName ?? item.supplierName ?? item.shipperName ?? "—"}
												</td>
												<td data-label="Sipariş / Rez.">
													{[item.sapDocNum ? `#${item.sapDocNum}` : null, item.reservationNo]
														.filter(Boolean)
														.join(" · ") || "—"}
												</td>
												<td data-label="Ürün">
													{lines.length > 0
														? `${lines.length} kalem`
														: item.stockItemCode
															? `${item.stockItemCode} – ${item.stockItemName}`
															: "—"}
												</td>
												<td data-label="Çıkış Tarihi">{formatDate(item.departureDate)}</td>
												<td data-label="Tahmini Varış">{formatDate(item.estimatedArrivalDate)}</td>
												<td data-label="Ekipman Tipi">{item.equipmentType}</td>
												<td data-label="Durum">
													<Badge variant={statusVariant[item.status] as never}>
														{STATUS_OPTIONS.find((s) => s.value === item.status)?.label ??
															item.status}
													</Badge>
												</td>
												<td data-label="İşlemler">
													<div className={styles.statusCell}>
														<StatusDropdown id={item.id} current={item.status} />
														<Button size="sm" onClick={() => openEditModal(item)}>
															Düzenle
														</Button>
														<Button
															size="sm"
															onClick={() => {
																if (confirm("Bu yük raporunu silmek istediğinizden emin misiniz?"))
																	deleteReport.mutate(item.id);
															}}
														>
															{t("delete")}
														</Button>
													</div>
												</td>
											</tr>
											{isExpanded && lines.length > 0 && (
												<tr key={`${item.id}-lines`}>
													<td
														colSpan={9}
														style={{ padding: 0, background: "var(--color-bg-sunken)" }}
													>
														<table className={tableStyles.table}>
															<thead>
																<tr>
																	<th>Kalem Kodu</th>
																	<th>Kalem Adı</th>
																	<th>Miktar</th>
																</tr>
															</thead>
															<tbody>
																{lines.map((line) => (
																	<tr key={line.id}>
																		<td data-label="Kalem Kodu">{line.itemCode}</td>
																		<td data-label="Kalem Adı">{line.itemDescription ?? "—"}</td>
																		<td data-label="Miktar">{line.quantity}</td>
																	</tr>
																))}
															</tbody>
														</table>
													</td>
												</tr>
											)}
										</>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(p) =>
						navigate({
							to: "/load-reports",
							search: { status, page: p, size: pageSize, search: searchQuery },
						})
					}
					onPageSizeChange={(s) =>
						navigate({
							to: "/load-reports",
							search: { status, page: 1, size: s, search: searchQuery },
						})
					}
				/>
			</Card>

			<Modal
				isOpen={modalOpen}
				onClose={() => {
					setModalOpen(false);
					setFormError("");
					setOrderLines([]);
					setSelectedLines({});
					setEditingId(null);
				}}
				title={editingId ? "Yük Raporunu Düzenle" : "Yeni Yük Raporu"}
				maxWidth={720}
			>
				<form onSubmit={handleSubmit} className={styles.form}>
					<FormField label="Kayıt Tipi" required>
						<select
							className={styles.filterSelect}
							value={mode}
							onChange={(e) => setMode(e.target.value as "international" | "domestic")}
						>
							<option value="international">Yurtdışı Sipariş</option>
							<option value="domestic">Firma (Yerel)</option>
						</select>
					</FormField>

					{mode === "international" ? (
						<>
							<FormField label="Yurtdışı Sipariş (No veya tedarikçi ara)" required>
								<SearchableSelect
									loadOptions={loadInternationalOrders}
									onChange={(val) => {
										const o = intlOrdersRef.current[val];
										setForm((f) => ({
											...f,
											sapDocEntry: val,
											sapDocNum: o ? String(o.docNum) : "",
											supplierCardCode: o?.cardCode ?? "",
											supplierName: o?.cardName ?? "",
											shipperName: f.shipperName || o?.cardName || "",
										}));
										setOrderLines(o?.lines ?? []);
										setSelectedLines({});
									}}
									value={form.sapDocEntry}
									placeholder="Sipariş no veya tedarikçi..."
								/>
							</FormField>

							{orderLines.length > 0 && (
								<FormField label="Sipariş Kalemleri (miktarıyla birlikte seçin)">
									<div
										className={tableStyles.wrapper}
										style={{
											border: "1px solid var(--color-border)",
											borderRadius: "var(--radius-md)",
										}}
									>
										<table className={tableStyles.table}>
											<thead>
												<tr>
													<th style={{ width: 32 }}></th>
													<th>Kalem</th>
													<th>Sipariş Miktarı</th>
													<th>Açık Miktar</th>
													<th style={{ width: 110 }}>Bu Sevkiyat</th>
												</tr>
											</thead>
											<tbody>
												{orderLines.map((line) => {
													const checked = selectedLines[line.lineNum] !== undefined;
													return (
														<tr key={line.lineNum}>
															<td>
																<input
																	type="checkbox"
																	checked={checked}
																	onChange={(e) => toggleOrderLine(line, e.target.checked)}
																/>
															</td>
															<td data-label="Kalem">
																{line.itemCode} – {line.description}
															</td>
															<td data-label="Sipariş Miktarı">{line.quantity}</td>
															<td data-label="Açık Miktar">{line.openQty}</td>
															<td data-label="Bu Sevkiyat">
																<Input
																	type="number"
																	min={0}
																	disabled={!checked}
																	value={selectedLines[line.lineNum] ?? ""}
																	onChange={(e) =>
																		setSelectedLines((prev) => ({
																			...prev,
																			[line.lineNum]: e.target.value,
																		}))
																	}
																/>
															</td>
														</tr>
													);
												})}
											</tbody>
										</table>
									</div>
								</FormField>
							)}
						</>
					) : (
						<FormField label="Firma" required>
							<SearchableSelect
								loadOptions={loadCompanies}
								onChange={(val) => setForm((f) => ({ ...f, companyId: val }))}
								value={form.companyId}
								placeholder="Firma ara..."
							/>
						</FormField>
					)}

					<div className={styles.formRow}>
						<FormField label="Rez. No">
							<Input
								value={form.reservationNo}
								onChange={(e) => setForm((f) => ({ ...f, reservationNo: e.target.value }))}
								placeholder="Örn: D1087"
							/>
						</FormField>
						<FormField label="Yükleyici">
							<Input
								value={form.shipperName}
								onChange={(e) => setForm((f) => ({ ...f, shipperName: e.target.value }))}
								placeholder="Örn: HUDSON"
							/>
						</FormField>
					</div>

					<div className={styles.formRow}>
						<FormField label="Çıkış Limanı">
							<Input
								value={form.departurePort}
								onChange={(e) => setForm((f) => ({ ...f, departurePort: e.target.value }))}
								placeholder="Örn: QINGDAO"
							/>
						</FormField>
						<FormField label="Varış Limanı">
							<Input
								value={form.arrivalPort}
								onChange={(e) => setForm((f) => ({ ...f, arrivalPort: e.target.value }))}
								placeholder="Örn: IZMIT"
							/>
						</FormField>
					</div>

					<div className={styles.formRow}>
						<FormField label="Booking No">
							<Input
								value={form.bookingNo}
								onChange={(e) => setForm((f) => ({ ...f, bookingNo: e.target.value }))}
							/>
						</FormField>
						<FormField label="HBL">
							<Input
								value={form.hblNo}
								onChange={(e) => setForm((f) => ({ ...f, hblNo: e.target.value }))}
							/>
						</FormField>
					</div>

					<FormField label="Gemi Adı">
						<Input
							value={form.vesselName}
							onChange={(e) => setForm((f) => ({ ...f, vesselName: e.target.value }))}
						/>
					</FormField>

					{mode === "domestic" && (
						<FormField label="Ürün (Koda veya isme göre ara)">
							<SearchableSelect
								loadOptions={loadStockItems}
								onChange={(val) => setForm((f) => ({ ...f, stockItemId: val }))}
								value={form.stockItemId}
								placeholder="Ürün kodu veya adı..."
								clearable
							/>
						</FormField>
					)}

					<div className={styles.formRow}>
						<FormField label="Çıkış Tarihi" required>
							<Input
								type="date"
								value={form.departureDate}
								onChange={(e) => setForm((f) => ({ ...f, departureDate: e.target.value }))}
							/>
						</FormField>
						<FormField label="Tahmini Varış Tarihi" required>
							<Input
								type="date"
								value={form.estimatedArrivalDate}
								onChange={(e) => setForm((f) => ({ ...f, estimatedArrivalDate: e.target.value }))}
							/>
						</FormField>
					</div>

					<FormField label="Ekipman Tipi" required>
						<Input
							value={form.equipmentType}
							onChange={(e) => setForm((f) => ({ ...f, equipmentType: e.target.value }))}
							placeholder="Örn: 20' Konteyner"
						/>
					</FormField>

					<FormField label="Notlar">
						<Input
							value={form.notes}
							onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
							placeholder="İsteğe bağlı not..."
						/>
					</FormField>

					{formError && (
						<p style={{ color: "var(--color-danger)", fontSize: "0.875rem" }}>{formError}</p>
					)}

					<div className={styles.formActions}>
						<Button
							type="button"
							variant="ghost"
							onClick={() => {
								setModalOpen(false);
								setOrderLines([]);
								setSelectedLines({});
								setEditingId(null);
							}}
						>
							{t("cancel")}
						</Button>
						<Button type="submit" disabled={createReport.isPending || updateReport.isPending}>
							{createReport.isPending || updateReport.isPending ? t("loading") : t("save")}
						</Button>
					</div>
				</form>
			</Modal>
		</div>
	);
}
